import {
  DashboardChartData,
  DashboardData,
  DashboardMetrics,
  ProtocolPerformance,
  UnprocessedCallsInsights
} from "../../types/metrics";
import { logError } from "../../utils/error";
import { getTimeRangeFilter, TimeRange } from "../../utils/timerange";
import { getUserTimezone } from "../../utils/timezone";
import { supabase } from "../supabase/client";

/**
 * Fetches all metrics for the dashboard in a single query
 * This minimizes the number of parallel requests and ensures consistency
 */
interface FetchDashboardMetricsParams {
  timeRange: TimeRange;
  timezone: string;
  protocol: string;
  user: string;
  callType: string;
  permittedEmails: string[] | null;
}

export const fetchDashboardMetrics = async (
  params: FetchDashboardMetricsParams
): Promise<DashboardMetrics | null> => {
  const { timeRange, timezone, protocol, user, callType, permittedEmails } = params;

  try {
    const { startDate, endDate } = getTimeRangeFilter(timeRange, timezone);

    const { data, error } = await supabase.rpc("get_dashboard_metrics", {
      start_date: startDate,
      end_date: endDate,
      protocol_filter: protocol,
      user_filter: user,
      call_type_filter: callType,
      permitted_emails: permittedEmails ?? undefined
    });

    if (error) {
      throw error;
    }
    if (!data) {
      return null;
    }

    // TODO: Remove cast once `get_dashboard_metrics` signature is changed from `returns jsonb` to `returns table(...)`.
    const row = data as unknown as {
      total_conversations: number;
      processed_conversations: number;
      processed_for_risk: number;
      total_scored: number;
      performance_0_1: number;
      performance_1_2: number;
      performance_2_3: number;
      performance_3_4: number;
      performance_4_5: number;
      sentiment_positive: number;
      sentiment_neutral: number;
      sentiment_negative: number;
      relevance_high: number;
      relevance_medium: number;
      relevance_low: number;
      risk_high: number;
      risk_medium: number;
      risk_low: number;
      average_score: number | null;
    };

    return {
      totalConversations: row.total_conversations,
      processedConversations: row.processed_conversations,
      processedForRisk: row.processed_for_risk,
      totalScored: row.total_scored,
      performance: {
        "0_1": row.performance_0_1,
        "1_2": row.performance_1_2,
        "2_3": row.performance_2_3,
        "3_4": row.performance_3_4,
        "4_5": row.performance_4_5
      },
      sentiment: {
        positive: row.sentiment_positive,
        neutral: row.sentiment_neutral,
        negative: row.sentiment_negative
      },
      relevance: {
        high: row.relevance_high,
        medium: row.relevance_medium,
        low: row.relevance_low
      },
      risk: {
        high: row.risk_high,
        medium: row.risk_medium,
        low: row.risk_low
      },
      averageScore: row.average_score || 0,
      previous: undefined
    };
  } catch (error) {
    logError("Error fetching dashboard metrics", error);
    return null;
  }
};

/**
 * Fetches data for the two charts in a single query
 * Groups by both sentiment and relevance to get the distribution
 */
interface FetchDashboardChartDataParams {
  timeRange: TimeRange;
  timezone: string;
  protocol: string;
  user: string;
  callType: string;
  permittedEmails: string[] | null;
}

export const fetchDashboardChartData = async (
  params: FetchDashboardChartDataParams
): Promise<DashboardChartData | null> => {
  const { timeRange, timezone, protocol, user, callType, permittedEmails } = params;

  try {
    const { startDate, endDate } = getTimeRangeFilter(timeRange, timezone);

    const { data, error } = await supabase.rpc("get_dashboard_chart_data", {
      start_date: startDate,
      end_date: endDate,
      protocol_filter: protocol,
      user_filter: user,
      call_type_filter: callType,
      permitted_emails: permittedEmails ?? undefined
    });

    if (error) {
      throw error;
    }
    if (!data) {
      return null;
    }

    // TODO: Remove cast once RPC signature is changed from `returns jsonb` to `returns table(...)`.
    return data as unknown as DashboardChartData;
  } catch (error) {
    logError("Error fetching dashboard chart data", error);
    return null;
  }
};
/**
 * Consolidated dashboard data fetching parameters
 */
interface FetchDashboardDataParams {
  timeRange: TimeRange;
  timezone: string;
  protocol: string;
  user: string;
  callType: string;
  permittedEmails: string[] | null;
}

/**
 * Fetches protocol performance data for the dashboard
 */
interface FetchProtocolPerformanceParams {
  timeRange?: TimeRange;
  timezone?: string;
  user?: string;
  permittedEmails: string[] | null;
}

export const fetchProtocolPerformance = async (
  params: FetchProtocolPerformanceParams
): Promise<ProtocolPerformance[]> => {
  const {
    timeRange = "thisMonth",
    timezone = getUserTimezone(),
    user = "all",
    permittedEmails
  } = params;

  try {
    const { startDate, endDate } = getTimeRangeFilter(timeRange, timezone);

    const { data, error } = await supabase.rpc("get_protocol_performance", {
      start_date: startDate,
      end_date: endDate,
      user_filter: user,
      permitted_emails: permittedEmails ?? undefined
    });

    if (error) {
      throw error;
    }
    if (!data) {
      return [];
    }

    // TODO: Remove cast once RPC signature is changed from `returns jsonb` to `returns table(...)`.
    const rows = data as unknown as ProtocolPerformance[];
    const totalConversations = rows.reduce((sum, protocol) => sum + protocol.conversationCount, 0);

    return rows.map(protocol => ({
      ...protocol,
      percentOfTotal: totalConversations > 0 ? (protocol.conversationCount / totalConversations) * 100 : 0
    }));

  } catch (error) {
    logError("Error fetching protocol performance", error);
    return [];
  }
};

interface FetchUnprocessedCallsInsightsParams {
  timeRange: TimeRange;
  timezone: string;
  protocol: string;
  user: string;
  callType: string;
  permittedEmails: string[] | null;
}

export const fetchUnprocessedCallsInsights = async (
  params: FetchUnprocessedCallsInsightsParams
): Promise<UnprocessedCallsInsights> => {
  const { timeRange, timezone, protocol, user, callType, permittedEmails } = params;

  try {
    const { startDate, endDate } = getTimeRangeFilter(timeRange, timezone);

    const { data, error } = await supabase.rpc("get_unprocessed_calls_insights", {
      start_date: startDate,
      end_date: endDate,
      protocol_filter: protocol,
      user_filter: user,
      call_type_filter: callType,
      permitted_emails: permittedEmails ?? undefined
    });

    if (error) {
      throw error;
    }
    if (!data) {
      return {
        unprocessedCalls: [],
        unscoredSummary: { count: 0, totalDurationMs: 0 }
      };
    }

    // TODO: Remove cast once RPC signature is changed from `returns jsonb` to `returns table(...)`.
    return data as unknown as UnprocessedCallsInsights;
  } catch (error) {
    logError("Error fetching unprocessed calls insights", error);
    return {
      unprocessedCalls: [],
      unscoredSummary: { count: 0, totalDurationMs: 0 }
    };
  }
};

/**
 * Fetches all dashboard data in a single optimized call
 * Combines metrics, chart data, and protocol performance
 */
export const fetchDashboardData = async (
  params: FetchDashboardDataParams
): Promise<DashboardData | null> => {
  const { timeRange, timezone, protocol, user, callType, permittedEmails } = params;

  try {
    const [metricsData, chartData, protocolPerformanceData] = await Promise.all([
      fetchDashboardMetrics({ timeRange, timezone, protocol, user, callType, permittedEmails }),
      fetchDashboardChartData({ timeRange, timezone, protocol, user, callType, permittedEmails }),
      fetchProtocolPerformance({ timeRange, timezone, user, permittedEmails })
    ]);

    if (!metricsData || !protocolPerformanceData) {
      throw new Error("Failed to fetch complete dashboard data");
    }

    return {
      metrics: metricsData,
      chartData: chartData,
      protocolPerformance: protocolPerformanceData
    };
  } catch (error) {
    logError("Error fetching consolidated dashboard data", error);
    return null;
  }
};
